/**
 * AnnouncementsModal — PanLuma's own delivery surface for user announcements.
 *
 * Shows eligible announcements one at a time after login. This is part of
 * PanLuma's dogfooding of the Software Factory module — other tenants consume
 * the `GET /announcements/me` API and render delivery in their own product.
 * See docs/platform/user-announcements.md.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ResponsiveDialog, HtmlDisplay } from '../../../../shared/components/common';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { announcementTypeColors } from '../announcementDisplay';
import type { SfUserAnnouncementDelivery } from '../../types';

interface AnnouncementsModalProps {
  open: boolean;
  announcements: SfUserAnnouncementDelivery[];
  index: number;
  onIndexChange: (index: number) => void;
  onDismiss: (id: string) => void;
  onClose: () => void;
}

export function AnnouncementsModal({
  open,
  announcements,
  index,
  onIndexChange,
  onDismiss,
  onClose,
}: AnnouncementsModalProps) {
  const { t } = useTranslation(['softwareFactory', 'common']);
  const current = announcements[index];

  // Close when the list empties out from underneath us (e.g. last item dismissed).
  useEffect(() => {
    if (open && announcements.length === 0) {
      onClose();
    }
  }, [open, announcements.length, onClose]);

  if (!current) {
    return null;
  }

  const isLast = index >= announcements.length - 1;

  const handleGotIt = () => {
    // Dismissing removes the item; the next one shifts into this index automatically.
    // If it was the last, the empty-list effect closes the modal.
    onDismiss(current.id);
  };

  const handleTellMeNextTime = () => {
    if (isLast) {
      onClose();
    } else {
      onIndexChange(index + 1);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={current.subject}
      className="max-w-lg"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <Badge className={announcementTypeColors[current.type] ?? ''}>
            {t(`softwareFactory:announcements.types.${current.type}`)}
          </Badge>
          {announcements.length > 1 && (
            <span className="text-xs text-muted-foreground">
              {t('common:announcementsModal.counter', {
                current: index + 1,
                total: announcements.length,
              })}
            </span>
          )}
        </div>

        {current.image_url && (
          <img
            src={current.image_url}
            alt={current.subject}
            className="max-h-48 w-full rounded-lg border border-border object-cover"
          />
        )}

        <HtmlDisplay content={current.body} />

        <div className="flex flex-col-reverse gap-2 py-2 px-1 sm:flex-row sm:justify-between">
          <Button variant="ghost" size="sm" onClick={handleTellMeNextTime}>
            {t('common:announcementsModal.tellMeNextTime')}
          </Button>
          <Button size="sm" onClick={handleGotIt}>
            {t('common:announcementsModal.gotIt')}
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
}
